import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2,
  ArrowRight,
  Phone,
  Handshake,
  Users,
  FileSignature,
  Factory,
  Search,
  // ShieldCheck,
  Target,
  Award
} from 'lucide-react';

export const SourcingPartnershipPackPage = () => {
  const problems = [
    'No reliable importer or distributor in the destination country',
    'Uncertainty about whether a local partner is properly licensed',
    'Manufacturers who cannot meet GMP or export documentation standards',
    'Agreements that leave pricing, liability, or compliance undefined',
    'A new manufacturing site that needs GMP approval before you can supply'
  ];

  const deliverables = [
    {
      icon: Search,
      title: 'Finding an Importer or Distributor in Your Target Market',
      lede:
        'This is where most exporters need the most help, and where we focus. We research licensed importers and distributors with strong local networks in your destination country, validate their credentials, licenses, and track record, and hand you a shortlist of vetted leads with full contact details, ready for you to engage.',
      bullets: [
        'Research licensed importers with strong local networks',
        'Validate credentials, licenses, and market experience',
        'Deliver vetted importer or distributor leads with contact details'
      ]
    },
    {
      icon: Factory,
      title: 'Finding a Manufacturer',
      lede:
        'If you are an importer or brand owner sourcing from India, we identify GMP and WHO-GMP certified manufacturers matched to your product range, quality needs, and export history, then deliver a shortlist of vetted leads.',
      bullets: [
        'Identify GMP and WHO-GMP certified manufacturers',
        'Shortlist by product range, quality, and export history',
        'Deliver vetted manufacturer leads with contact details'
      ]
    },
    {
      icon: FileSignature,
      title: 'Agreements & Negotiations',
      lede:
        'A good partner still needs a clear contract. We draft and review agreements between manufacturers, importers, and regulatory partners, covering pricing, liability, timelines, and compliance clauses, and support you through to final signing.',
      bullets: [
        'Draft and review contracts with manufacturers, importers, and RA partners',
        'Cover pricing, liability, timelines, and compliance clauses',
        'Support negotiations through to final signing'
      ]
    }
    // {
    //   icon: ShieldCheck,
    //   title: 'GMP and New-Site Application Support',
    //   lede:
    //     'Entering a market may require a new or approved manufacturing site. We prepare the application package with SOPs, QC documents, and CoPP, run a gap analysis against the GMP checklist, and liaise with the Ministry of Health through to approval.',
    //   bullets: [
    //     'Prepare the application package with SOPs, QC docs, and CoPP',
    //     'Conduct a gap analysis against the GMP checklist',
    //     'Liaise with the Ministry of Health through to approval'
    //   ]
    // }
  ];

  const processSteps = [
    'Share your product and your target market',
    'We research and verify manufacturers or importers in that market',
    'You receive a vetted shortlist with contact details',
    'We support agreements and any GMP application through to signing'
  ];

  const idealFor = [
    'Exporters who need a trusted importer or distributor in a new market',
    'Importers and brands sourcing GMP-certified manufacturers from India',
    'Manufacturers expanding into markets where they have no local partner',
    'Companies formalizing supply agreements or opening a new GMP site'
  ];

  const faqs = [
    {
      q: 'Can you help me find an importer in my target export market?',
      a: 'Yes. Finding a licensed importer or distributor in your destination country is the core of the Sourcing & Partnership Pack. We research importers with strong local networks, verify their licenses, credentials, and track record, and give you a shortlist of vetted leads with contact details, ready to engage.'
    },
    {
      q: 'How do you verify importers and distributors?',
      a: "We validate each partner's regulatory licenses, trading credentials, and market experience before recommending them. Only importers and distributors that pass this check appear on your shortlist, so you are never introduced to an unverified partner."
    },
    {
      q: 'Can you find a GMP-certified manufacturer in India?',
      a: 'Yes. For importers and brands sourcing from India, we identify GMP and WHO-GMP certified manufacturers matched to your product range, quality requirements, and export history, and deliver a shortlist of vetted leads with contact details.'
    },
    {
      q: 'Do you help with the agreements between manufacturer and importer?',
      a: 'Yes. We draft and review contracts covering pricing, liability, timelines, and compliance clauses, and support you through negotiation to final signing, so the partnership is protected on both sides.'
    },
    {
      q: 'What if I need a new GMP manufacturing site approved?',
      a: 'The pack includes GMP and new-site application support. We prepare the application with SOPs, QC documents, and CoPP, run a gap analysis against the GMP checklist, and liaise with the Ministry of Health through to approval.'
    }
  ];

  // JSON-LD schemas
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Sourcing & Partnership Pack',
    'alternateName': 'Pharma Importer & Manufacturer Sourcing',
    'serviceType': 'Pharmaceutical Importer and Manufacturer Sourcing, GMP Support',
    'description':
      'Find and verify licensed importers or distributors in your target export market, identify GMP-certified manufacturers, draft supply agreements, and complete GMP or new-site applications for pharmaceutical and nutraceutical exports.',
    'provider': { '@type': 'Organization', 'name': 'TheApostrophe', 'url': 'https://www.theapostrophe.com' },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America'],
    'url': 'https://www.theapostrophe.com/success-packs/sourcing-partnership-pack',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.theapostrophe.com/success-packs/sourcing-partnership-pack'
    }
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How the Sourcing & Partnership Pack works',
    'description':
      'A four-step process to find, verify and contract the right importer or manufacturer for your pharma export market.',
    'step': processSteps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': `Step ${i + 1}`,
      'text': s
    }))
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Success Packs', 'item': 'https://www.theapostrophe.com/success-packs' },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': 'Sourcing & Partnership Pack',
        'item': 'https://www.theapostrophe.com/success-packs/sourcing-partnership-pack'
      }
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Find Importers & Manufacturers for Pharma Export Markets</title>
        <meta
          name="description"
          content="Connect with vetted importers and GMP-certified manufacturers in your target export market. Sourcing, agreements and GMP support for pharma and nutra exporters."
        />
        <meta
          name="keywords"
          content="find importer in target market, pharma importer sourcing, GMP certified manufacturer sourcing, licensed pharma distributor, importer distributor for pharma export, manufacturer partnership India, new site GMP application support, WHO-GMP manufacturer India, pharma export sourcing"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/success-packs/sourcing-partnership-pack" />

        {/* Open Graph */}
        <meta property="og:title" content="Find Importers & Manufacturers for Pharma Export Markets" />
        <meta property="og:description" content="Connect with vetted importers and GMP-certified manufacturers in your target export market. Sourcing, agreements and GMP support for pharma and nutra exporters." />
        <meta property="og:url" content="https://www.theapostrophe.com/success-packs/sourcing-partnership-pack" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Find Importers & Manufacturers for Pharma Export Markets" />
        <meta name="twitter:description" content="Vetted importers and GMP manufacturers, supply agreements, and GMP application support for pharma & nutra exporters." />

        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2" style={{ backgroundColor: '#A07050', color: 'white' }} data-testid="sourcing-pack-badge">
            Success Pack 1
          </Badge>
          <div className="w-24 h-24 rounded-2xl bg-white/60 flex items-center justify-center mb-6 mx-auto shadow-md">
            <Handshake className="h-12 w-12" style={{ color: '#A07050' }} aria-hidden="true" />
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            style={{ color: '#303040' }}
            data-testid="sourcing-pack-h1"
          >
            Find the Right Importer and Manufacturer in Your Target Market
          </h1>
          <p className="text-xl md:text-2xl font-bold mb-8" style={{ color: '#A07050' }}>
            Build the manufacturing and distribution backbone your export business runs on.
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed" style={{ color: '#606070' }}>
            A great product does not reach a new market on its own. It needs a licensed importer who knows the local regulator, a manufacturer who can meet export and GMP standards, and agreements that protect you on both sides. The Sourcing &amp; Partnership Pack finds these partners for you, verifies them, and helps you sign with confidence, so you enter your target market with the right people already in place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A07050', color: 'white' }}
                data-testid="sourcing-start-btn"
              >
                Find My Market Partner
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#A07050', color: '#A07050' }}
                data-testid="sourcing-book-call-btn"
              >
                Talk to a Sourcing Expert
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What It Solves */}
      <section className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: '#303040' }}>
            What It Solves
          </h2>
          <p className="text-base md:text-lg text-center mb-12 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Most export plans stall not on the product, but on the partners. Common problems we remove:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {problems.map((p, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-6 rounded-xl transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
              >
                <div
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white text-sm"
                  style={{ backgroundColor: '#A07050' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {p}
                </span>
              </div>
            ))}
          </div>
          <p className="text-base md:text-lg text-center mt-12 max-w-3xl mx-auto italic" style={{ color: '#606070' }}>
            The Sourcing &amp; Partnership Pack gives you verified partners and clean agreements, so your market entry starts on solid ground.
          </p>
        </div>
      </section>

      {/* What You Get - Deliverables */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            What You Get
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Three defined deliverables that turn your target market into a working supply and distribution setup.
          </p>
          <div className="space-y-10">
            {deliverables.map((d, i) => (
              <article
                key={i}
                className="rounded-3xl p-8 md:p-12 shadow-md hover:shadow-xl transition-shadow"
                style={{ backgroundColor: 'white' }}
              >
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
                  <div className="flex md:flex-col items-start gap-4 md:gap-3">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#A07050' }}
                    >
                      <d.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <div className="text-4xl font-bold" style={{ color: '#A07050', opacity: 0.5 }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                      {d.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#606070' }}>
                      {d.lede}
                    </p>
                    <div className="space-y-2">
                      {d.bullets.map((b, bi) => (
                        <div key={bi} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#A07050' }} aria-hidden="true" />
                          <span className="text-base" style={{ color: '#505060' }}>
                            {b}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 112, 80, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Process
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg"
                  style={{ backgroundColor: '#A07050' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <p className="text-base md:text-lg leading-relaxed pt-2" style={{ color: '#505060' }}>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="h-8 w-8" style={{ color: '#A07050' }} aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#303040' }}>
              Ideal For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {idealFor.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-6 rounded-xl" style={{ backgroundColor: '#F8F4EE' }}>
                <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#A07050' }} aria-hidden="true" />
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 md:p-12 rounded-3xl" style={{ backgroundColor: 'rgba(160, 112, 80, 0.12)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-7 w-7" style={{ color: '#A07050' }} aria-hidden="true" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#A07050' }}>
                Why This Matters
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#505060' }}>
              The right partner is the difference between a market you enter once and a market you stay in. A verified importer clears your product faster, a compliant manufacturer keeps your registration secure, and a clear agreement protects the relationship for years. This pack gives you all three before you commit.
            </p>
          </div>
        </div>
      </section>

      {/* Retainer CTA Block */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 shadow-xl" style={{ backgroundColor: 'white' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#A060B0' }}
              >
                <Users className="h-10 w-10 text-white" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                  Want your entire regulatory workflow handled continuously?
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#708080' }}>
                  Once your partners are in place, the <strong>Apostrophe Retainer</strong> gives you one dedicated regulatory affairs expert managing every stage on an ongoing basis, so you never have to hire, train, or run an in-house regulatory team.
                </p>
                <Link to="/retainer">
                  <Button
                    size="lg"
                    className="px-6 py-5 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    data-testid="sourcing-retainer-btn"
                  >
                    Explore the Retainer Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 112, 80, 0.08)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready to Find Your Market Partner?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#606070' }}>
            Tell us your product and target market. We will find and verify the right importer or manufacturer for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A07050', color: 'white' }}
                data-testid="sourcing-cta-find-partner-btn"
              >
                Find My Market Partner
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
                data-testid="sourcing-cta-book-call-btn"
              >
                Book a Call
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ - AEO */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Straight answers about sourcing importers, manufacturers, agreements and GMP support.
          </p>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
                open={i === 0}
              >
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-4"
                  data-testid={`sourcing-faq-${i}`}
                >
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {f.q}
                  </h3>
                  <span
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-45"
                    style={{ backgroundColor: '#A07050', color: 'white' }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-base md:text-lg leading-relaxed mt-4" style={{ color: '#606070' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
